import React from "react";
import { Typography, Grid, Container, Paper, Avatar, Box } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { Helmet } from "react-helmet";

const Profile = () => {
  const user = JSON.parse(localStorage.getItem("user"));

  if (!user) {
    return (
      <Container component="main" maxWidth="lg" disableGutters sx={{ p: 0 }}>
        <Typography variant="h6" align="center">
          No user data available.
        </Typography>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{user.name || "Profile"} | Sitepilot</title>
        <meta
          name="description"
          content={`View profile details of ${
            user.name || "user"
          } on sitepilot.online.`}
        />
        <meta
          name="keywords"
          content={`${
            user.name || "user"
          }, profile, user profile, sitepilot, account details`}
        />
      </Helmet>

      <Container
        component="main"
        maxWidth="lg"
        sx={{
          position: "relative",
          px: { xs: 2, sm: 3 },
          py: { xs: 3, sm: 4 },
        }}
      >
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              letterSpacing: "-0.02em",
              mb: 1.5,
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            Profile
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ fontWeight: 400, fontSize: { xs: "1rem", md: "1.25rem" } }}
          >
            View and manage your account information
          </Typography>
        </Box>

        <Box
          sx={{
            maxWidth: 800,
            mx: "auto",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              background:
                "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              p: { xs: 4, sm: 6 },
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "16px 16px 0 0",
              },
            }}
          >
            {/* Avatar Section */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Avatar
                sx={{
                  width: { xs: 80, sm: 120 },
                  height: { xs: 80, sm: 120 },
                  mx: "auto",
                  mb: 3,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)",
                  border: "4px solid rgba(255, 255, 255, 0.9)",
                }}
              >
                <AccountCircleIcon sx={{ fontSize: { xs: 60, sm: 90 } }} />
              </Avatar>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  letterSpacing: "-0.02em",
                  mb: 1,
                }}
              >
                {user.name || "N/A"}
              </Typography>

              <Typography
                variant="h6"
                color="text.secondary"
                sx={{
                  fontWeight: 400,
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                }}
              >
                {user.email}
              </Typography>
            </Box>

            {/* Details Section */}
            <Box
              sx={{
                mt: 4,
                pt: 4,
                borderTop: "1px solid rgba(0, 0, 0, 0.08)",
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: "rgba(102, 126, 234, 0.05)",
                      border: "1px solid rgba(102, 126, 234, 0.1)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background: "rgba(102, 126, 234, 0.08)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)",
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        mb: 1,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontSize: "0.75rem",
                      }}
                    >
                      Role
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                      }}
                    >
                      {user.role || "User"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: "rgba(102, 126, 234, 0.05)",
                      border: "1px solid rgba(102, 126, 234, 0.1)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background: "rgba(102, 126, 234, 0.08)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)",
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        mb: 1,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontSize: "0.75rem",
                      }}
                    >
                      User ID
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        fontFamily: "monospace",
                        fontSize: "0.9rem",
                      }}
                    >
                      {user.id}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default Profile;
